import { Button, FieldWrapper, Icons, Label } from '@commenda/kakashi';
import {
  EventHandlerEventType,
  TBaseHumanStepDefinition,
  TEventHandlerDefinition,
} from '@commenda/types';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { DEFAULT_EVENT_HANDLER_ACTION } from './EventHandlerActionEditor';
import { EventHandlerCard } from './EventHandlerCard';
import { DEFAULT_EVENT_HANDLER_SCHEDULE } from './EventHandlerScheduleEditor';

const DEFAULT_EVENT_TYPE = EventHandlerEventType.STEP_ATTEMPT_STARTED;

const getNextEventHandlerKey = (eventHandlers: TEventHandlerDefinition[]) => {
  const keys = new Set(eventHandlers.map((eventHandler) => eventHandler.key));
  let index = eventHandlers.length + 1;
  while (keys.has(`event-handler-${index}`)) index++;
  return `event-handler-${index}`;
};

export const StepEventHandlersConfig = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as TBaseHumanStepDefinition | undefined;

  if (!selectedStep) return null;

  const eventHandlers = selectedStep.eventHandlers ?? [];

  const setEventHandlers = (nextEventHandlers: TEventHandlerDefinition[]) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id
          ? {
              ...step,
              eventHandlers: nextEventHandlers.length
                ? nextEventHandlers
                : undefined,
            }
          : step
      ),
    });
  };

  const addEventHandler = () => {
    setEventHandlers([
      ...eventHandlers,
      {
        id: crypto.randomUUID(),
        key: getNextEventHandlerKey(eventHandlers),
        name: `Event handler ${eventHandlers.length + 1}`,
        when: { eventType: DEFAULT_EVENT_TYPE },
        schedule: DEFAULT_EVENT_HANDLER_SCHEDULE,
        action: DEFAULT_EVENT_HANDLER_ACTION,
      },
    ]);
  };

  return (
    <FieldWrapper>
      <Label className="flex items-center justify-between">
        <span className="grow">Event handlers</span>
        <Button size="sm" onClick={addEventHandler}>
          <Icons.Plus className="size-4" />
        </Button>
      </Label>
      {eventHandlers.length === 0 && (
        <span className="text-text-secondary txt-medium-regular">
          Run an action if this step is still open after a set time.
        </span>
      )}
      {eventHandlers.map((eventHandler) => (
        <EventHandlerCard
          key={eventHandler.id}
          eventHandler={eventHandler}
          ownerStepId={selectedEntity.id}
          onChange={(nextEventHandler) =>
            setEventHandlers(
              eventHandlers.map((existing) =>
                existing.id === eventHandler.id ? nextEventHandler : existing
              )
            )
          }
          onDelete={() =>
            setEventHandlers(
              eventHandlers.filter((existing) => existing.id !== eventHandler.id)
            )
          }
        />
      ))}
    </FieldWrapper>
  );
};
